import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNostr } from '@/hooks/useNostr';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';

export type ContactStatus = 'pending' | 'accepted' | 'declined' | 'blocked';
export type ContactDirection = 'sent' | 'received';

export interface ContactRequest {
  id: string;
  fromPubkey: string;
  toPubkey: string;
  fromNickname: string;
  toNickname: string;
  message?: string;
  status: ContactStatus;
  direction: ContactDirection;
  timestamp: Date;
}

// Custom kind for contact requests (above 30000 as recommended)
const CONTACT_REQUEST_KIND = 34201;
const CONTACT_RESPONSE_KIND = 34202;

export function useSendContactRequest() {
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent } = useNostrPublish();

  return useMutation({
    mutationFn: async ({
      toNickname,
      toPubkey,
      message
    }: {
      toNickname: string;
      toPubkey: string;
      message?: string;
    }) => {
      if (!user?.signer) throw new Error('User not authenticated');

      const contactRequestEvent = {
        kind: CONTACT_REQUEST_KIND,
        content: JSON.stringify({
          from_pubkey: user.pubkey,
          from_nickname: user.profile?.name || '',
          to_pubkey: toPubkey,
          to_nickname: toNickname,
          message: message || '',
          type: 'contact_request'
        }),
        created_at: Math.floor(Date.now() / 1000),
        tags: [
          ['p', toPubkey],
          ['to_nickname', toNickname]
        ]
      };

      await publishEvent(contactRequestEvent);
      return contactRequestEvent;
    }
  });
}

export function useRespondToContactRequest() {
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent } = useNostrPublish();

  return useMutation({
    mutationFn: async ({
      originalRequestId,
      fromPubkey,
      response
    }: {
      originalRequestId: string;
      fromPubkey: string;
      response: 'accepted' | 'declined';
    }) => {
      if (!user?.signer) throw new Error('User not authenticated');

      const responseEvent = {
        kind: CONTACT_RESPONSE_KIND,
        content: JSON.stringify({
          original_request_id: originalRequestId,
          from_pubkey: fromPubkey,
          to_pubkey: user.pubkey,
          response,
          type: 'contact_response'
        }),
        created_at: Math.floor(Date.now() / 1000),
        tags: [
          ['p', fromPubkey],
          ['e', originalRequestId],
          ['response', response]
        ]
      };

      await publishEvent(responseEvent);
      return responseEvent;
    }
  });
}

export function useContactRequests() {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['contact-requests', user?.pubkey],
    queryFn: async (): Promise<ContactRequest[]> => {
      if (!user) return [];

      // Query both sent and received contact requests
      const [sentRequests, receivedRequests] = await Promise.all([
        // Sent requests: find events FROM our pubkey
        nostr.query([
          {
            kinds: [CONTACT_REQUEST_KIND],
            authors: [user.pubkey],
            limit: 100
          }
        ]),
        // Received requests: find events TO our pubkey
        nostr.query([
          {
            kinds: [CONTACT_REQUEST_KIND],
            '#p': [user.pubkey],
            limit: 100
          }
        ], { signal: AbortSignal.timeout(5000) })
      ]);

      const contacts: ContactRequest[] = [];

      // Process sent requests
      for (const event of sentRequests) {
        try {
          const data = JSON.parse(event.content);
          const toPubkey = event.tags.find(tag => tag[0] === 'p')?.[1] || data.to_pubkey;

          contacts.push({
            id: event.id,
            fromPubkey: user.pubkey,
            toPubkey: toPubkey,
            fromNickname: data.from_nickname || '',
            toNickname: data.to_nickname || '',
            message: data.message,
            status: 'pending', // Sent requests are pending until response
            direction: 'sent',
            timestamp: new Date(event.created_at * 1000)
          });
        } catch (e) { }
      }

      // Process received requests
      for (const event of receivedRequests) {
        try {
          const data = JSON.parse(event.content);
          const fromPubkey = event.tags.find(tag => tag[0] === 'p')?.[1] || data.from_pubkey;

          contacts.push({
            id: event.id,
            fromPubkey: fromPubkey,
            toPubkey: user.pubkey,
            fromNickname: data.from_nickname || '',
            toNickname: data.to_nickname || '',
            message: data.message,
            status: 'pending', // Received requests are pending until we respond
            direction: 'received',
            timestamp: new Date(event.created_at * 1000)
          });
        } catch (e) { }
      }

      // Remove duplicates and sort by timestamp
      const uniqueContacts = contacts.filter((contact, index, self) =>
        index === self.findIndex(c => c.id === contact.id)
      );

      return uniqueContacts.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    },
    enabled: !!user,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 5 * 1000, // Refresh every 5 seconds for new requests
  });
}

export function useAcceptedContacts() {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['accepted-contacts', user?.pubkey],
    queryFn: async (): Promise<{ pubkey: string; nickname: string; addedAt: Date; }[]> => {
      if (!user) return [];

      // In production, this should query accepted contact relationships
      // For now, return mock accepted contacts
      return [
        {
          pubkey: 'mock-pubkey-contact1',
          nickname: 'usuario123',
          addedAt: new Date()
        }
      ];
    },
    enabled: !!user,
    staleTime: 60 * 1000, // 1 minute
  });
}